/**
 * A few Android browsers report their collapsible browser toolbar as a CSS
 * safe-area inset. That turns the app's 56px chrome and compact player into
 * large blank bands. Android browser UI already sits outside the layout
 * viewport, so it must not be reserved as an app safe area. Standalone/PWA
 * displays are excluded: they may genuinely need their display cutout inset.
 */
const viewportSyncCleanup = new WeakMap<Document, () => void>();

function syncVisibleViewportHeight(doc: Document, view: Window): void {
	const height = view.visualViewport?.height ?? view.innerHeight;
	if (!Number.isFinite(height) || height <= 0) return;
	doc.documentElement.style.setProperty('--app-visible-viewport-height', `${Math.round(height * 100) / 100}px`);
}

function bindVisibleViewportHeight(doc: Document, view: Window): void {
	if (viewportSyncCleanup.has(doc)) return;
	const sync = () => syncVisibleViewportHeight(doc, view);
	view.visualViewport?.addEventListener('resize', sync);
	view.addEventListener('resize', sync);
	viewportSyncCleanup.set(doc, () => {
		view.visualViewport?.removeEventListener('resize', sync);
		view.removeEventListener('resize', sync);
	});
}

export function applyAndroidViewportInsetPolicy(doc: Document = document): void {
	const view = doc.defaultView;
	const userAgent = view?.navigator.userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
	const standaloneNavigator = view?.navigator as (Navigator & { standalone?: boolean }) | undefined;
	const isStandalone = view?.matchMedia?.('(display-mode: standalone)').matches === true || standaloneNavigator?.standalone === true;
	const isAndroidBrowser = /Android/i.test(userAgent) && !isStandalone;
	doc.documentElement.toggleAttribute('data-android-browser', isAndroidBrowser);
	if (isAndroidBrowser && view) {
		syncVisibleViewportHeight(doc, view);
		bindVisibleViewportHeight(doc, view);
		return;
	}
	viewportSyncCleanup.get(doc)?.();
	viewportSyncCleanup.delete(doc);
	doc.documentElement.style.removeProperty('--app-visible-viewport-height');
}
